import React, { useEffect, useState } from 'react';
import api from '../../services/api';
import toast from 'react-hot-toast';
import wardsData from '../../data/wards.json';

export default function SaraiZoneRouting() {
  const [depts, setDepts]       = useState([]);
  const [zones, setZones]       = useState([]);
  const [nocTypes, setNocTypes] = useState([]);
  const [saraiNoc, setSaraiNoc] = useState(null);
  const [saving, setSaving]     = useState(false);
  const [loading, setLoading]   = useState(true);

  // Zone → dept mapping (editable)
  const [zoneDepts, setZoneDepts] = useState({
    1: '', 2: '', 3: '', 4: '', 5: ''
  });

  // Sarai routing for each zone
  const [zoneRouting, setZoneRouting] = useState({});

  const wards = wardsData.wards || [];
  const staticZones = wardsData.zones || [];

  useEffect(() => {
    const init = async () => {
      setLoading(true);
      try {
        const [deptsRes, nocRes] = await Promise.all([
          api.get('/masters/departments'),
          api.get('/masters/noc-types'),
        ]);
        const allDepts = deptsRes.departments || [];
        const allNoc   = nocRes.noc_types || [];
        setDepts(allDepts);
        setNocTypes(allNoc);

        // Find Sarai NOC
        const sarai = allNoc.find(n =>
          n.noc_name?.toLowerCase().includes('sarai') ||
          n.noc_code?.toLowerCase().includes('sarai')
        );
        setSaraiNoc(sarai);

        // Load zone → dept from zones table
        try {
          const zonesRes = await api.get('/zones');
          const z = zonesRes.zones || [];
          setZones(z);
          const map = {};
          z.forEach(zone => { map[zone.id] = zone.dept_id || ''; });
          setZoneDepts(map);
        } catch {
          setZones(staticZones.map((z, i) => ({ ...z, dept_id: null })));
        }

        // Load routing for Sarai NOC
        if (sarai) {
          try {
            const r = await api.get(`/workflow-routing/${sarai.id}`);
            const routing = {};
            (r.routing || []).forEach(step => {
              routing[step.sequence_order] = {
                dept_id: step.dept_id,
                is_final: step.is_final_approval
              };
            });
            setZoneRouting(routing);
          } catch {}
        }
      } finally {
        setLoading(false);
      }
    };
    init();
  }, []);

  const saveZoneDept = async (zoneId) => {
    try {
      await api.put('/zones/dept', { zone_id: zoneId, dept_id: zoneDepts[zoneId] || null });
      toast.success(`Zone ${zoneId} department saved!`);
    } catch {
      toast.error('Save failed');
    }
  };

  const saveAllZoneDepts = async () => {
    setSaving(true);
    try {
      for (const [zoneId, deptId] of Object.entries(zoneDepts)) {
        await api.put('/zones/dept', { zone_id: parseInt(zoneId), dept_id: deptId || null });
      }
      toast.success('All zone departments saved!');
    } catch {
      toast.error('Save failed');
    } finally {
      setSaving(false);
    }
  };

  const saveSaraiRouting = async () => {
    if (!saraiNoc) return toast.error('Sarai NOC not found. Create it first in NOC Types.');
    setSaving(true);
    try {
      // Build steps: related depts (if any) + all zone depts as final
      // Simple routing: User → Zone Dept (final)
      // If you have a related dept before zone, add it as step 1
      const steps = [];
      let seq = 1;

      // Check if there's a common related dept set for Sarai
      if (saraiNoc.related_dept_id) {
        steps.push({
          dept_id: saraiNoc.related_dept_id,
          sequence_order: seq++,
          is_final_approval: 0
        });
      }

      // Zone depts as final — we save ONE routing for Sarai
      // Zone routing is dynamic (handled by zone_id on application)
      // For now save the base routing without zone-specific depts
      // Zone-specific routing is applied at submission time based on zone_id

      if (steps.length === 0 && Object.values(zoneDepts).some(d => d)) {
        // Use Zone 1 dept as default final (can be changed per application)
        const firstZoneDept = Object.values(zoneDepts).find(d => d);
        if (firstZoneDept) {
          steps.push({ dept_id: parseInt(firstZoneDept), sequence_order: 1, is_final_approval: 1 });
        }
      }

      if (steps.length > 0) {
        await api.post('/workflow-routing', {
          noc_type_id: saraiNoc.id,
          steps,
        });
        toast.success('Sarai base routing saved!');
      } else {
        toast.error('Please assign at least one zone department first');
      }
    } catch {
      toast.error('Save failed');
    } finally {
      setSaving(false);
    }
  };

  if (loading) return (
    <div className="flex items-center justify-center py-20">
      <div className="animate-spin w-8 h-8 border-2 border-blue-600 border-t-transparent rounded-full" />
    </div>
  );

  return (
    <div>
      <div className="mb-6">
        <h1 className="text-2xl font-bold text-gray-800">🏘️ Sarai NOC — Zone Routing</h1>
        <p className="text-sm text-gray-500 mt-1">
          Assign departments to zones. Sarai applications route to zone-specific department.
        </p>
      </div>

      {/* Info */}
      <div className="p-4 bg-blue-50 border border-blue-200 rounded-xl text-sm text-blue-800 mb-6">
        <p className="font-semibold mb-1">ℹ️ Sarai NOC Routing Logic:</p>
        <div className="text-xs text-blue-700 space-y-1">
          <p>1. Citizen applies for Sarai NOC → selects Ward → Zone auto-detected</p>
          <p>2. Application goes to <strong>Zone Department</strong> (assigned below)</p>
          <p>3. If Related Department is set in Workflow Routing Master → goes there first</p>
          <p>4. Last department = Final Approval</p>
        </div>
        <div className="mt-2 p-2 bg-white rounded-lg text-xs font-mono text-gray-600">
          User → [Related Dept optional] → Zone 1 Office / Zone 2 Office / ... → ✅ Approved
        </div>
      </div>

      {/* Sarai NOC Status */}
      <div className={`p-3 rounded-xl border mb-6 text-sm ${saraiNoc ? 'bg-green-50 border-green-200 text-green-700' : 'bg-red-50 border-red-200 text-red-700'}`}>
        {saraiNoc
          ? `✅ Sarai NOC found: "${saraiNoc.noc_name}" (Code: ${saraiNoc.noc_code})`
          : '❌ Sarai NOC not found! Go to Admin → NOC Types → Create a NOC type with "Sarai" in the name.'}
      </div>

      {/* Zone → Department Mapping */}
      <div className="card mb-6">
        <div className="flex items-center justify-between mb-4">
          <div>
            <h3 className="font-bold text-gray-800">Zone → Department Assignment</h3>
            <p className="text-xs text-gray-500 mt-0.5">
              Each zone will route Sarai applications to its assigned department
            </p>
          </div>
          <button onClick={saveAllZoneDepts} disabled={saving}
            className="btn-primary text-sm">
            {saving ? '⏳ Saving...' : '💾 Save All'}
          </button>
        </div>

        <div className="space-y-3">
          {staticZones.map(zone => {
            const zoneWards = wards.filter(w => w.zone_id === zone.id);
            return (
              <div key={zone.id}
                className="flex items-center gap-4 p-4 border-2 border-gray-100 rounded-xl hover:border-blue-100">
                {/* Zone info */}
                <div className="w-24 flex-shrink-0">
                  <div className="w-10 h-10 bg-blue-100 rounded-full flex items-center justify-center text-blue-700 font-bold text-sm mb-1">
                    Z{zone.id}
                  </div>
                  <p className="text-xs font-semibold text-gray-700">{zone.zone_name}</p>
                  <p className="text-xs text-gray-400">
                    Wards: {zoneWards.map(w => w.ward_no).join(', ')}
                  </p>
                </div>

                {/* Arrow */}
                <div className="text-gray-300 text-2xl flex-shrink-0">→</div>

                {/* Department selector */}
                <div className="flex-1">
                  <label className="text-xs text-gray-500 mb-1 block">Final Approval Department</label>
                  <select
                    className="form-select text-sm"
                    value={zoneDepts[zone.id] || ''}
                    onChange={e => setZoneDepts(p => ({ ...p, [zone.id]: e.target.value }))}>
                    <option value="">-- Not Assigned --</option>
                    {depts.map(d => (
                      <option key={d.id} value={d.id}>{d.dept_name} ({d.dept_type})</option>
                    ))}
                  </select>
                </div>

                {/* Status */}
                <div className="flex-shrink-0">
                  {zoneDepts[zone.id]
                    ? <span className="text-xs bg-green-100 text-green-700 border border-green-200 px-2 py-1 rounded-full">✅ Assigned</span>
                    : <span className="text-xs bg-red-50 text-red-400 border border-red-200 px-2 py-1 rounded-full">⚠️ Not set</span>}
                </div>
              </div>
            );
          })}
        </div>
      </div>

      {/* Ward → Zone Reference Table */}
      <div className="card mb-6">
        <h3 className="font-bold text-gray-800 mb-3">📋 Ward → Zone Reference</h3>
        <p className="text-xs text-gray-500 mb-3">
          Update <code className="bg-gray-100 px-1 rounded">frontend/src/data/wards.json</code> to change ward-zone mapping.
        </p>
        <div className="overflow-x-auto">
          <table className="data-table">
            <thead>
              <tr>
                <th>Ward No</th>
                <th>Ward Name</th>
                <th>Zone</th>
                <th>Dept (assigned above)</th>
              </tr>
            </thead>
            <tbody>
              {wards.map(w => {
                const zone = staticZones.find(z => z.id === w.zone_id);
                const deptId = zoneDepts[w.zone_id];
                const dept = depts.find(d => String(d.id) === String(deptId));
                return (
                  <tr key={w.id}>
                    <td className="font-semibold text-blue-600">Ward {w.ward_no}</td>
                    <td>{w.ward_name}</td>
                    <td>
                      <span className="bg-blue-50 text-blue-700 border border-blue-200 text-xs px-2 py-0.5 rounded-full font-medium">
                        {zone?.zone_name || '—'}
                      </span>
                    </td>
                    <td className="text-xs text-gray-600">{dept?.dept_name || '—'}</td>
                  </tr>
                );
              })}
            </tbody>
          </table>
        </div>
      </div>

      {/* Save Sarai Base Routing */}
      {saraiNoc && (
        <div className="card">
          <h3 className="font-bold text-gray-800 mb-2">⚙️ Save Sarai Base Routing</h3>
          <p className="text-xs text-gray-500 mb-4">
            This sets the initial workflow for Sarai NOC. Zone-specific routing happens automatically at submission based on the citizen's selected zone.
          </p>
          <div className="p-3 bg-yellow-50 border border-yellow-200 rounded-xl text-xs text-yellow-700 mb-4">
            ⚠️ For Related Department (e.g., Vinyash) before Zone — set it in <strong>Workflow Routing Master</strong> page for Sarai NOC.
          </div>
          <button onClick={saveSaraiRouting} disabled={saving} className="btn-success">
            {saving ? '⏳ Saving...' : '💾 Save Sarai Routing'}
          </button>
        </div>
      )}
    </div>
  );
}